import { createFileRoute, redirect, Outlet, Link, useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useRoles } from "@/hooks/use-roles";
import { Settings } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getUnreadContactCount } from "@/lib/contact.functions";

export const Route = createFileRoute("/_authenticated/admin")({
  beforeLoad: async ({ location }) => {
    // Skip auth on SSR; localStorage-backed session isn't available there.
    if (typeof window === "undefined") return;
    // getUser() awaits session hydration from storage, so this is safe right after login.
    const { data: u, error: userError } = await supabase.auth.getUser();
    if (userError || !u.user) throw redirect({ to: "/login", search: { redirect: location.href } });
    const { data: role, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", u.user.id)
      .eq("role", "admin")
      .maybeSingle();
    if (error) {
      console.error("[admin gate] role check failed", error);
      throw redirect({ to: "/" });
    }
    if (!role) throw redirect({ to: "/" });
  },
  head: () => ({
    meta: [
      { title: "Admin Console — Pluggz" },
      { name: "description", content: "Internal admin console for Pluggz operators: review creators, commissions, products and audit activity." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminLayout,
});

const MAIN_NAV = [
  { to: "/admin", label: "Overview", exact: true },
  { to: "/admin/creators", label: "Creators", exact: false },
  { to: "/admin/commissions", label: "Commissions", exact: false },
  { to: "/admin/products", label: "Products", exact: false },
  { to: "/admin/looks", label: "Looks", exact: false },
  { to: "/admin/affiliates", label: "Affiliates", exact: false },
  { to: "/admin/payouts", label: "Payouts", exact: false },
  { to: "/admin/audit", label: "Audit log", exact: false },
  { to: "/admin/contact", label: "Inbox", exact: false },
  { to: "/admin/partnerships", label: "Partnerships", exact: false },
] as const;

const SETTINGS_NAV = { to: "/admin/settings", label: "Settings", exact: false } as const;

function AdminLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useRoles();
  const fetchUnread = useServerFn(getUnreadContactCount);

  const { data: unreadData } = useQuery({
    queryKey: ["admin-contact-unread"],
    queryFn: () => fetchUnread(),
    refetchInterval: 60_000,
    enabled: !!user && isAdmin,
  });
  const unreadCount = unreadData?.count ?? 0;

  useEffect(() => {
    if (authLoading || adminLoading) return;
    if (!user) {
      navigate({ to: "/login", search: { redirect: pathname } });
      return;
    }
    if (!isAdmin) {
      navigate({ to: "/" });
    }
  }, [adminLoading, authLoading, isAdmin, navigate, pathname, user]);

  if (authLoading || adminLoading || !user || !isAdmin) {
    return (
      <div className="mx-auto flex min-h-[40vh] max-w-6xl items-center justify-center px-6 py-10">
        <p className="text-sm text-muted-foreground">Checking admin access…</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-10">
      <p className="editorial-eyebrow">Operator</p>
      <h1 className="mt-2 font-serif text-3xl sm:text-4xl">Admin console</h1>

      <div className="mt-5 -mx-4 sm:mx-0 overflow-x-auto border-b border-border">
        <nav aria-label="Admin navigation" className="flex gap-0.5 whitespace-nowrap px-4 sm:px-0">
          {MAIN_NAV.map((n) => {
            const active = n.exact ? pathname === n.to : pathname.startsWith(n.to);
            const isInbox = n.label === "Inbox";
            return (
              <Link
                key={n.to}
                to={n.to}
                className={`flex-shrink-0 -mb-px inline-flex items-center gap-2 border-b-2 px-2 py-1.5 sm:px-3 text-[10px] sm:text-[11px] uppercase tracking-widest transition-colors ${
                  active
                    ? "border-foreground text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {n.label}
                {isInbox && unreadCount > 0 && (
                  <span className="inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">
                    {unreadCount}
                  </span>
                )}
              </Link>
            );
          })}
          <div className="mx-1 h-4 w-px shrink-0 self-center bg-border" />
          {(() => {
            const n = SETTINGS_NAV;
            const active = n.exact ? pathname === n.to : pathname.startsWith(n.to);
            return (
              <Link
                key={n.to}
                to={n.to}
                className={`flex-shrink-0 -mb-px inline-flex items-center gap-1.5 border-b-2 px-2 py-1.5 sm:px-3 text-[10px] sm:text-[11px] uppercase tracking-widest transition-colors ${
                  active
                    ? "border-foreground text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <Settings className="h-3.5 w-3.5" />
                {n.label}
              </Link>
            );
          })()}
        </nav>
      </div>

      <div className="mt-6 sm:mt-8">
        <Outlet />
      </div>
    </div>
  );
}
